import React, { useState, useEffect, useMemo, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { ROWS_PER_PAGE_OPTIONS, trainingFormStatusLabels } from "../constant/constants.js"; // Example: [2, 5, 10, 20]
import { GridOverlay } from "@mui/x-data-grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { SearchQueryMatch } from "../constant/utils/commonUtils.js";
import {
  buildPinnedRightColumnSx,
  getPinnedRightFields,
  normalizeColumnsForPinnedLayout,
} from "../utils/dataGridPinnedColumns.js";

const CDataGrid = ({
  columns,
  rows,
  loading = false,
  columnVisibilityModel,
  searchQuery = "",
  checkboxSelection = false,
  idKey,
  getRowHeight,
  className,
  initialPageSize = 50,
  height = "100%",
  autoHeight = true,
  pinActionsColumn = true,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    sx: externalSx,
    disableVirtualization: externalDisableVirtualization,
    ...restProps
  } = props;
  const [rowHeight, setRowHeight] = useState(52);
  const [headerHeight, setHeaderHeight] = useState(52);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(400);

  // Measure parent height when component mounts or when height prop changes
  useEffect(() => {
    if (height === "100%" && containerRef.current) {
      const updateHeight = () => {
        const parent = containerRef.current?.parentElement;
        if (parent) {
          const parentHeight = parent.offsetHeight;
          if (parentHeight && parentHeight > 0) {
            setContainerHeight(parentHeight);
          }
        }
      };

      // Initial measurement
      updateHeight();

      // Use setTimeout to ensure parent has rendered
      const timeoutId = setTimeout(updateHeight, 0);

      // Listen for resize events
      window.addEventListener("resize", updateHeight);

      // Use ResizeObserver for more accurate parent size tracking
      let resizeObserver;
      if (containerRef.current?.parentElement) {
        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const newHeight = entry.contentRect.height;
            if (newHeight && newHeight > 0) {
              setContainerHeight(newHeight);
            }
          }
        });
        resizeObserver.observe(containerRef.current.parentElement);
      }

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("resize", updateHeight);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, [height]);


 const filteredRows = useMemo(() => {
        if (!searchQuery) return rows;

        const lowerCaseSearchQuery = searchQuery.toLowerCase();
        return rows?.filter((row) =>
            Object.values(row).some(topLevelValue =>
                SearchQueryMatch(topLevelValue, lowerCaseSearchQuery)
            )
        );
    }, [rows, searchQuery]);

  // Responsive height adjustment
  useEffect(() => {
    const handleResize = () => {
      setRowHeight(getRowHeight ? getRowHeight : 44);
      setHeaderHeight(getRowHeight ? getRowHeight : 52);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getRowHeight]);

  const CustomLoadingOverlay = () => (
    <GridOverlay>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        <CircularProgress size={30} />
        <Typography sx={{ mt: 1 }}>Processing data...</Typography>
      </Box>
    </GridOverlay>
  );

  // Show when no rows after loading
  const CustomNoRowsOverlay = () => (
    <GridOverlay>
      <Typography color="textSecondary">No rows</Typography>
    </GridOverlay>
  );

  // Determine the actual height to use
  // If height is "100%", use measured containerHeight, otherwise use the provided height or default
  const actualHeight = height === "100%" 
    ? (containerHeight > 0 ? containerHeight : 400) 
    : (height || 400);

  const pinnedRightFields = useMemo(
    () => getPinnedRightFields(columns, pinActionsColumn),
    [columns, pinActionsColumn]
  );

  const shouldPinActions = pinnedRightFields.length > 0;

  const pinnedColumnSx = useMemo(
    () => (shouldPinActions ? buildPinnedRightColumnSx(pinnedRightFields) : {}),
    [shouldPinActions, pinnedRightFields]
  );

  const gridColumns = useMemo(
    () =>
      shouldPinActions
        ? normalizeColumnsForPinnedLayout(columns, pinnedRightFields, {
            compact: isMobile,
          })
        : columns,
    [columns, pinnedRightFields, shouldPinActions, isMobile]
  );

  const disableVirtualization =
    externalDisableVirtualization ?? (shouldPinActions ? true : undefined);

  const gridClassName = [
    "xl:!text-sm xl:!leading-sm !text-xs !leading-xs xl:row-height-52 row-height-36",
    className,
    shouldPinActions ? "c-data-grid--pinned-actions" : "",
    shouldPinActions && isMobile ? "c-data-grid--pinned-actions-mobile" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div 
      ref={containerRef} 
      style={{
        width: "100%",
        minWidth: 0,
        height: height === "100%" ? "100%" : actualHeight,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <DataGrid
        rows={filteredRows}
        columns={gridColumns}
        getRowId={(row) => row[idKey]}
        columnVisibilityModel={columnVisibilityModel}
        disableRowSelectionOnClick
        disableColumnMenu
        getRowHeight={getRowHeight}
        checkboxSelection={checkboxSelection}
        pagination
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={({ page, pageSize }) => {
          setPage(page);
          setPageSize(pageSize);
        }}
        pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
        loading={loading}
        autoHeight={false}
        disableVirtualization={disableVirtualization}
        slots={{
          loadingOverlay: CustomLoadingOverlay,
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        className={gridClassName}
        sx={{
          width: "100%",
          height: actualHeight,
          flex: 1,
          "& .MuiDataGrid-columnHeaders": {
            position: "sticky",
            top: 0,
            backgroundColor: "#e5f3fa",
            zIndex: 1000,
          },
          "& .MuiDataGrid-footerContainer": {
            position: "sticky",
            bottom: 0,
            backgroundColor: "#e5f3fa",
            zIndex: 1000,
            borderTop: "1px solid #aae3ff",
          },
          "& .MuiDataGrid-root": {
            overflow: "hidden",
            minWidth: 0,
          },
          "& .MuiDataGrid-main": {
            overflow: "hidden",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
            overflowY: "auto",
          },
          ...(shouldPinActions && {
            "& .MuiDataGrid-virtualScrollerContent": {
              minWidth: "max-content",
            },
            "& .MuiDataGrid-columnHeadersInner": {
              minWidth: "max-content",
            },
          }),
          ...pinnedColumnSx,
          ...externalSx,
        }}
        {...restProps}
      />
    </div>
  );
};

export default CDataGrid;
